/**
 * components/BiayaLainEditor.js
 * The transaction form's "Biaya Lain-Lain (tidak ditagih)" box (2026-10-11, the cost system the
 * client confirmed). Our OWN costs for this transaction — a driver tip, a truck, bongkar muat, a
 * broker — that nobody pays us back, so they never change Total Nilai.
 *
 * Presentational, like CostItemsEditor: the parent (TransactionFormModal) owns `rows`/`onChange`
 * and gates Simpan on `utils/expenses.js`'s `checkExpenseRows`. Works the SAME in create and
 * edit mode — this box IS the one place a linked cost is changed (Edit Transaksi), the same
 * place the old in-transaction "biaya" rows were edited. Each row: Kategori (required), Dibayar
 * ke (optional), Jumlah (> 0), Keterangan (optional). A row loaded from the books carries its
 * `id` (a correction); a row added here has none (new). Saved as Biaya Lain-Lain records linked
 * to this transaction (`data.expenses`), shown on the Pengeluaran page.
 */
import React from "react";
import { formatCurrencyInput, parseCurrencyInput } from "../utils/currency";
import { EXPENSE_CATEGORIES, checkExpenseRows, sumExpenses } from "../utils/expenses";
import { fmtQty } from "../utils/idGenerators";

export default function BiayaLainEditor({ rows = [], onChange, txnType }) {
  const set = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const check = checkExpenseRows(rows);

  return (
    <div className="card" data-testid="biaya-lain">
      <div className="card__title">Biaya Lain-Lain (tidak ditagih)</div>
      <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>
        Biaya yang kita bayar sendiri ke pihak lain dan tidak ditagih (tip sopir, truk, bongkar muat, komisi pihak luar).
        {txnType === "PEMBELIAN" && " Untuk pembelian, ini ikut masuk ke modal (HPP) barangnya, dibagi menurut nilai barang."}
      </div>
      {rows.length === 0 && <div className="form-hint" style={{ marginBottom: "var(--gap-sm)" }}>Belum ada baris.</div>}
      {rows.map((r, i) => (
        <div className="biaya-lain-row" key={r.id || `new-${i}`} style={{ marginBottom: "var(--gap-sm)" }}>
          <div style={{ display: "flex", gap: "var(--gap-md)", alignItems: "flex-end" }}>
            <div className="form-row" style={{ flex: "0 0 auto", marginBottom: 0 }}>
              <label className="form-label" htmlFor={`biaya-lain-kategori-${i}`}>Kategori</label>
              <select id={`biaya-lain-kategori-${i}`} className="form-select" value={r.category} onChange={(e) => set(i, { category: e.target.value })}>
                <option value="">— Pilih kategori —</option>
                {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                {r.category && !EXPENSE_CATEGORIES.includes(r.category) && <option value={r.category}>{r.category}</option>}
              </select>
            </div>
            <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label" htmlFor={`biaya-lain-paidto-${i}`}>Dibayar ke</label>
              <input id={`biaya-lain-paidto-${i}`} className="form-input" placeholder="opsional, mis. Pak Budi (sopir)"
                value={r.paidTo} onChange={(e) => set(i, { paidTo: e.target.value })} />
            </div>
            <div className="form-row" style={{ flex: 1, marginBottom: 0 }}>
              <label className="form-label" htmlFor={`biaya-lain-jumlah-${i}`}>Jumlah</label>
              <div className="currency-input">
                <span className="currency-input__prefix">Rp</span>
                <input id={`biaya-lain-jumlah-${i}`} type="text" inputMode="numeric" value={formatCurrencyInput(r.amount)}
                  onChange={(e) => set(i, { amount: parseCurrencyInput(e.target.value) })} />
              </div>
            </div>
            <button type="button" className="btn btn-ghost btn-sm" title="Hapus biaya lain-lain"
              onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>✕</button>
          </div>
          <div className="form-row" style={{ marginTop: "var(--gap-xs)", marginBottom: 0 }}>
            <label className="form-label" htmlFor={`biaya-lain-keterangan-${i}`}>Keterangan</label>
            <input id={`biaya-lain-keterangan-${i}`} className="form-input" placeholder="opsional"
              value={r.note} onChange={(e) => set(i, { note: e.target.value })} />
          </div>
        </div>
      ))}
      <button type="button" className="btn btn-sm" onClick={() => onChange([...rows, { category: "", paidTo: "", amount: null, note: "" }])}>
        + Biaya Lain-Lain
      </button>
      {rows.length > 0 && check.ok && (
        <div className="form-hint" style={{ marginTop: "var(--gap-sm)" }}>Total biaya lain-lain: Rp {fmtQty(sumExpenses(rows))}</div>
      )}
      {!check.ok && <div className="form-hint biaya-lain-error" role="alert" style={{ color: "var(--red)", marginTop: "var(--gap-sm)" }}>{check.error}</div>}
    </div>
  );
}
